import React from 'react';
import { getPriceOffPercentage } from '../templates/product-listing-page/PlpUtils/ProcessBadgeDiscount';
import { gatedSaleState } from '../templates/product/components/GatedSaleProductPrice/state';
import { GatedSaleConfigurations } from '../templates/product/components/GatedSaleProductPrice/types';
import { fetchDataFromBuilder, getBuilderCacheBusterKey } from './builder';

export const fetchGatedSaleConfigFromBuilder = async function () {
  if (gatedSaleState.isConfigFetched) return; // Prevent duplicate fetches

  const maxRetries = 5;
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      const data = await fetchDataFromBuilder<GatedSaleConfigurations>('sale-configurations', {
        options: { cb: getBuilderCacheBusterKey() },
      }).then((resp) => resp?.data || null);

      if (data && typeof data === 'object' && !Array.isArray(data)) {
        gatedSaleState.setGatedSaleState(data);
      } else {
        console.warn('Invalid exclusive sale configuration data received from Builder:', data);
      }

      return; // Success, exit the function
    } catch (error) {
      attempt++;
      console.warn(`Error fetching exclusive sale config from Builder (attempt ${attempt}/${maxRetries}):`, error);

      if (attempt >= maxRetries) {
        return; // Give up after max retries
      }

      // Wait for 1s before retrying
      const delay = 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

export const updatePercentageInMemberGatedBadge = (props: { badge: string; price: number; compareAtPrice: number }) => {
  const { badge, price, compareAtPrice } = props;
  const percentageMatch = badge.match(/(\S+)%/);
  if (percentageMatch) {
    const percentage = getPriceOffPercentage(price, compareAtPrice);
    return badge.replace(/(\S+)%/, `${percentage}%`);
  }
  return badge;
};

export const getGatedSalePriceWithLabel = (formattedPrice: string) => {
  const { memberGatedSaleConfig, appGatedSaleConfig } = gatedSaleState;

  const gatedSaleCopy = appGatedSaleConfig?.isEnabled
    ? appGatedSaleConfig?.priceAppendix
    : memberGatedSaleConfig?.isEnabled
    ? memberGatedSaleConfig?.priceAppendix
    : null;

  return gatedSaleCopy ? gatedSaleCopy.replace(/%S/i, formattedPrice) : null;
};

export const getGatedSalePotentialSavingsCopy = (formattedPrice: string) => {
  const { potentialSavingsCopy } = gatedSaleState;

  return potentialSavingsCopy ? potentialSavingsCopy.replace(/%S/i, formattedPrice) : null;
};

export const getGatedSalePriceLabelColor = () => {
  const { memberGatedSaleConfig, appGatedSaleConfig, hardMarkSaleConfig } = gatedSaleState;

  return appGatedSaleConfig?.isEnabled
    ? appGatedSaleConfig?.textColor
    : memberGatedSaleConfig?.isEnabled
    ? memberGatedSaleConfig?.textColor
    : hardMarkSaleConfig?.isEnabled
    ? hardMarkSaleConfig?.saleColor
    : '';
};

export const getGatedSaleProperties = (): {
  isMemberGatedEnabled: boolean;
  isAppGatedEnabled: boolean;
  containerClassName: string;
  badgeStyle: React.CSSProperties;
} => {
  const { isEnabled: isMemberGatedEnabled, memberGatedBadgeColor } = gatedSaleState.memberGatedSaleConfig;
  const { isEnabled: isAppGatedEnabled, textColor: appTextColor } = gatedSaleState.appGatedSaleConfig;
  const { isEnabled: isHardMarkSaleEnabled, saleColor: hardMarkSaleColor } = gatedSaleState.hardMarkSaleConfig;

  const badgeStyle: React.CSSProperties = isMemberGatedEnabled
    ? ({
        '--member-gated-badge-color': memberGatedBadgeColor || 'var(--alert-medium)',
      } as React.CSSProperties)
    : isAppGatedEnabled
    ? ({
        '--gated-sale-price-color': appTextColor || 'var(--alert-medium)',
      } as React.CSSProperties)
    : isHardMarkSaleEnabled
    ? ({
        '--hard-mark-sale-color': hardMarkSaleColor || 'var(--alert-medium)',
      } as React.CSSProperties)
    : {};

  const containerClassName = isMemberGatedEnabled
    ? 'member-gated'
    : isAppGatedEnabled
    ? 'app-gated'
    : isHardMarkSaleEnabled
    ? 'hard-mark-sale'
    : '';

  return { isMemberGatedEnabled, isAppGatedEnabled, containerClassName, badgeStyle };
};
